import { DateTime, Interval } from "luxon"

type ArbitraryTime = string | Date | DateTime | number
type Nullable<T> = T | null

export default function useTimeUtils() {
  function isValueString(value: ArbitraryTime): value is string {
    return typeof value === "string"
  }

  function isValueJsDate(value: ArbitraryTime): value is Date {
    return Object.prototype.toString.call(value) === "[object Date]"
  }

  function isValueLuxonDate(value: ArbitraryTime): value is DateTime {
    return DateTime.isDateTime(value)
  }

  function isValueNumber(value: ArbitraryTime): value is number {
    return typeof value === "number"
  }

  const convertToLuxon = (value: ArbitraryTime): DateTime => {
    if (isValueString(value)) return DateTime.fromISO(value)
    if (isValueNumber(value)) return DateTime.fromSeconds(value)
    if (isValueJsDate(value)) return DateTime.fromJSDate(value)
    if (isValueLuxonDate(value)) return value
    throw new DOMException("Failed to convert val to Luxon", "TimeUtilError")
  }

  const toISODateStr = (value?: Nullable<ArbitraryTime>): string => {
    if (!value) return ""
    return convertToLuxon(value).toISO()!.split("T")[0] // 2023-08-06
  }

  const toCompactDateStr = (value?: Nullable<ArbitraryTime>): string => {
    if (!value) return ""
    return convertToLuxon(value).toFormat("D") // 8/6/2023
  }

  const toCompactDateTimeStr = (value?: Nullable<ArbitraryTime>): string => {
    if (!value) return ""
    return convertToLuxon(value).toFormat("D t") // 8/6/2023 1:07 PM
  }

  const toDateStr = (date: ArbitraryTime): string | undefined => {
    if (date) return convertToLuxon(date).toFormat("DD") // Aug 6, 2014
  }

  const toTimeStr = (datetime: ArbitraryTime): string | undefined => {
    if (datetime) return convertToLuxon(datetime).toFormat("t") // 9:07 AM
  }

  const toDateTimeStr = (datetime: ArbitraryTime): string | undefined => {
    if (datetime) return convertToLuxon(datetime).toFormat("ff") // Aug; 6, 2014, 1:07 PM
  }

  const toWeekdayDateTimeStr = (date: ArbitraryTime): string => {
    return convertToLuxon(date).toFormat("ccc, ff") // Wed, Aug 6, 2014, 1:07 PM
  }

  const toTimeRangeStr = (time1: ArbitraryTime, time2: ArbitraryTime): string => {
    return `${toTimeStr(time1)} - ${toTimeStr(time2)}` // 9:07 AM - 9:07 AM
  }

  const toWeekdayDateTimeRangeStr = (time1: ArbitraryTime, time2: ArbitraryTime): string => {
    return `${toWeekdayDateTimeStr(time1)} - ${toTimeStr(time2)}` // Wed, Aug 6, 2014 9:07 AM - 9:07 AM
  }

  const toIntervalInMinutes = (start: DateTime, end: DateTime): number => {
    const interval = Interval.fromDateTimes(start, end)
    const minutes = Math.ceil(interval.length("minutes"))
    return minutes > 0 ? minutes : 0
  }

  const toRelativeDueDateStr = (datetime: ArbitraryTime): string => {
    const given = convertToLuxon(datetime)
    const relativeStr = given.toRelativeCalendar()
    if (relativeStr) {
      if (relativeStr.includes("ago") || relativeStr.includes("yesterday")) {
        return "Overdue"
      }
      return relativeStr.charAt(0).toUpperCase() + relativeStr.slice(1).toLowerCase()
    }
    return ""
  }

  const toRelativeTimeAgo = (datetime: ArbitraryTime): string => {
    const now = DateTime.now()
    const dateTimeObj = convertToLuxon(datetime)

    if (dateTimeObj > now) {
      return "Recently"
    }

    const interval = Interval.fromDateTimes(dateTimeObj, now)
    const numDays = interval.length("days")

    if (numDays >= 0) {
      return "Recently"
    } else if (numDays == -1) {
      return "1 day ago"
    } else {
      return `${numDays} days ago`
    }
  }

  const isSameDay = (date1: DateTime, date2: DateTime): boolean => {
    return date1.hasSame(date2, "day")
  }

  const isPastDateTime = (datetime: DateTime): boolean => {
    const now = DateTime.now()
    datetime = convertToLuxon(datetime)
    return datetime < now
  }

  const isPastDate = (date: DateTime): boolean => {
    const now = DateTime.now().startOf("day")
    date = convertToLuxon(date).startOf("day")
    return date < now
  }

  const dateYearsAgo = (years: number) => {
    const currentDate = new Date() // Current date and time
    const timestamp18YearsAgo = currentDate.getTime() - years * 365.24 * 24 * 60 * 60 * 1000 // Timestamp representing 18 years ago
    return new Date(timestamp18YearsAgo)
  }

  const getClientTimezone = (): string => {
    return DateTime.now().zoneName
  }

  return {
    convertToLuxon,
    toISODateStr,
    toCompactDateStr,
    toCompactDateTimeStr,
    toDateStr,
    toTimeStr,
    toDateTimeStr,
    toWeekdayDateTimeStr,
    toTimeRangeStr,
    toWeekdayDateTimeRangeStr,
    toIntervalInMinutes,
    toRelativeDueDateStr,
    toRelativeTimeAgo,
    isSameDay,
    isPastDateTime,
    isPastDate,
    dateYearsAgo,
    getClientTimezone
  }
}
